/**
 * `V` — select and move (§9.3).
 *
 * click = pick · drag on a fill = move the feature · drag on a vertex = move
 * it · drag on an edge midpoint = insert a vertex and move it · drag on empty
 * canvas = marquee · `Shift` = add to the selection · `Alt+click` on a vertex
 * = delete it.
 */
import { distPct, idle, none, withState, type ToolEffect, type ToolHandler, type ToolMachine, type ToolResult } from './types'

export const selectTool: ToolHandler = {
  id: 'select',
  shortcut: 'v',
  icon: 'MousePointer2',
  label: 'Select',
  hint: 'Click a shape to pick it. Drag it to move. Drag a dot to reshape.',
  cursor: 'default',

  reduce(machine: ToolMachine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      const { hit } = ev

      // Alt+click a vertex deletes it — no gesture, one history entry.
      if (hit.id && hit.vertex !== undefined && ev.alt) {
        return idle('select', [{ k: 'beginGesture' }, { k: 'deleteVertex', id: hit.id, index: hit.vertex }, { k: 'endGesture' }])
      }

      if (hit.id && hit.vertex !== undefined) {
        return withState('select', { t: 'vertex.dragging', id: hit.id, index: hit.vertex, start: ev.at }, [
          { k: 'select', ids: [hit.id], additive: ev.shift },
          { k: 'selectVertex', id: hit.id, index: hit.vertex },
          { k: 'beginGesture' },
        ])
      }

      // Dragging an edge midpoint inserts a vertex there and drags the new one.
      if (hit.id && hit.edge !== undefined) {
        const index = hit.edge + 1
        return withState('select', { t: 'vertex.dragging', id: hit.id, index, start: ev.at }, [
          { k: 'select', ids: [hit.id], additive: ev.shift },
          { k: 'beginGesture' },
          { k: 'insertVertex', id: hit.id, index, at: ev.at },
          { k: 'selectVertex', id: hit.id, index },
        ])
      }

      if (hit.id) {
        const ids = ev.shift ? uniq([...cfg.selection, hit.id]) : cfg.selection.includes(hit.id) ? cfg.selection : [hit.id]
        const origin = ids.map((id) => cfg.ringsById(id)).filter((r): r is NonNullable<typeof r> => r !== undefined)
        return withState('select', { t: 'feature.dragging', ids, start: ev.at, origin }, [
          { k: 'select', ids, additive: ev.shift },
          { k: 'beginGesture' },
        ])
      }

      return withState('select', { t: 'marquee', from: ev.at, to: ev.at, additive: ev.shift }, ev.shift ? [] : [{ k: 'clearSelection' }])
    }

    if (ev.t === 'pointermove') {
      if (st.t === 'vertex.dragging') {
        return withState('select', st, [{ k: 'moveVertex', id: st.id, index: st.index, to: ev.at }])
      }
      if (st.t === 'feature.dragging') {
        // The TOTAL offset since the press: the store applies it to the pre-drag geometry, so
        // moves never compound (a +10 drag used to move the shape +15).
        const delta: [number, number] = [ev.at[0] - st.start[0], ev.at[1] - st.start[1]]
        return withState('select', st, [{ k: 'translateFeatures', ids: st.ids, delta, fromStart: true }])
      }
      if (st.t === 'marquee') {
        return withState('select', { ...st, to: ev.at })
      }
      return none(machine)
    }

    if (ev.t === 'pointerup') {
      if (st.t === 'vertex.dragging' || st.t === 'feature.dragging') {
        return idle('select', [{ k: 'endGesture' }])
      }
      if (st.t === 'marquee') {
        const effects: ToolEffect[] =
          distPct(st.from, st.to) < cfg.dragThreshold ? [] : [{ k: 'selectInRect', from: st.from, to: st.to, additive: st.additive }]
        return idle('select', effects)
      }
      return none(machine)
    }

    if (ev.t === 'pointercancel' || ev.t === 'cancel') {
      if (st.t === 'vertex.dragging' || st.t === 'feature.dragging') return idle('select', [{ k: 'cancelGesture' }])
      return idle('select')
    }

    return null
  },
}

function uniq(xs: string[]): string[] {
  return [...new Set(xs)]
}
